/**
 * ActivityPage.jsx — v1.0.0 Gate 5 Item 1 (Unified Gamification Home)
 *
 * Single home for all gamification. Layout top→bottom:
 *   1. Points total (prominent)
 *   2. Current level/tier
 *   3. Badge collection (earned + locked silhouettes)
 *   4. Punch card tiers (color-coded progress)
 *   5. Points history (last 10)
 *   6. Contribution history (last 5)
 *
 * No gamification surfaced elsewhere in the app.
 */
import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { API, axios } from "@/api";
import { BottomNav } from "@/components/shared/Navigation";
import { Badge, BadgeCell } from "@/components/shared/Badge";
import { motion } from "framer-motion";
import {
  Zap, Loader2, Coffee, Award, ChevronRight, Sparkles, X, FileText,
} from "lucide-react";

const LEVELS = [
  { name: "Starter", min: 0 },
  { name: "Explorer", min: 100 },
  { name: "Regular", min: 500 },
  { name: "Enthusiast", min: 1500 },
  { name: "Insider", min: 5000 },
  { name: "Legend", min: 15000 },
];

function pickLevel(points) {
  let current = LEVELS[0];
  let next = LEVELS[1];
  for (let i = 0; i < LEVELS.length; i++) {
    if (points >= LEVELS[i].min) {
      current = LEVELS[i];
      next = LEVELS[i + 1] || null;
    }
  }
  return { current, next };
}

export default function ActivityPage() {
  const { user } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [points, setPoints] = useState(0);
  const [catalog, setCatalog] = useState([]);
  const [earnedCount, setEarnedCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [punchCards, setPunchCards] = useState([]);
  const [pointsHistory, setPointsHistory] = useState([]);
  const [contributions, setContributions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [badgeDetail, setBadgeDetail] = useState(null);

  useEffect(() => {
    if (!user) return;
    setLoading(true);
    Promise.all([
      axios.get(`${API}/gamification/summary`).catch(() => ({ data: {} })),
      axios.get(`${API}/gamification/badges-catalog`).catch(() => ({ data: { catalog: [], earned_count: 0, total_count: 0 } })),
      axios.get(`${API}/gamification/punch-tiers`).catch(() => ({ data: { punch_cards: [] } })),
      axios.get(`${API}/gamification/points-history?limit=10`).catch(() => ({ data: [] })),
      axios.get(`${API}/contributions/my`).catch(() => ({ data: { items: [] } })),
    ]).then(([sumRes, catRes, punchRes, histRes, contribRes]) => {
      const s = sumRes.data || {};
      setPoints(s.points || s.total_points || 0);
      setCatalog(catRes.data?.catalog || []);
      setEarnedCount(catRes.data?.earned_count || 0);
      setTotalCount(catRes.data?.total_count || 0);
      setPunchCards(punchRes.data?.punch_cards || []);
      setPointsHistory(histRes.data || []);
      setContributions(contribRes.data?.items || []);
    }).finally(() => setLoading(false));
  }, [user]);

  const { current: currentLevel, next: nextLevel } = useMemo(() => pickLevel(points), [points]);
  const progressPct = nextLevel
    ? Math.min(100, Math.round(((points - currentLevel.min) / (nextLevel.min - currentLevel.min)) * 100))
    : 100;

  return (
    <div className="min-h-screen pb-24" style={{ background: theme.pageBg }} data-testid="activity-page">
      {/* ══ 1 & 2. Points total + level ══ */}
      <div className="px-5 pt-6 pb-5 text-white" style={{ background: theme.headerGradient }}>
        <h1 className="text-base font-bold tracking-wide uppercase opacity-80" data-testid="activity-title">
          Your Activity
        </h1>
        <div className="flex items-baseline gap-2 mt-2">
          <Zap className="w-7 h-7 text-amber-300 self-center" />
          <span className="text-5xl font-black tracking-tight" data-testid="activity-points-total">
            {points.toLocaleString()}
          </span>
          <span className="text-white/60 text-sm">points</span>
        </div>
        {/* Level + progress */}
        <div className="mt-4 flex items-center gap-2" data-testid="activity-level">
          <Sparkles className="w-3.5 h-3.5 text-white/60" />
          <span className="text-xs font-semibold text-white/90" data-testid="activity-level-name">
            {currentLevel.name}
          </span>
          {nextLevel && (
            <span className="text-[10px] text-white/50">
              {points.toLocaleString()} / {nextLevel.min.toLocaleString()}
            </span>
          )}
        </div>
        {nextLevel && (
          <div className="mt-2 h-1.5 rounded-full bg-white/10 overflow-hidden" data-testid="activity-level-progress">
            <div className="h-full rounded-full bg-amber-300 transition-all" style={{ width: `${progressPct}%` }} />
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-10">
          <Loader2 className="w-6 h-6 animate-spin" style={{ color: theme.primary }} />
        </div>
      ) : (
        <div className="px-4 pt-4 space-y-4">
          {/* ══ 3. Badges — earned + locked silhouettes ══ */}
          <section
            className="bg-white rounded-2xl p-4 border border-slate-100 shadow-sm"
            data-testid="activity-badges-section"
          >
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-bold text-slate-800">Badge Collection</h2>
              <span className="text-xs text-slate-500" data-testid="activity-badges-count">
                {earnedCount} / {totalCount} earned
              </span>
            </div>
            {catalog.length > 0 ? (
              <div className="grid grid-cols-4 gap-1">
                {catalog.slice(0, 16).map((b) => (
                  <BadgeCell
                    key={b.badge_id}
                    badge={b}
                    onClick={() => setBadgeDetail(b)}
                  />
                ))}
              </div>
            ) : (
              <p className="text-xs text-slate-500">No badges defined yet.</p>
            )}
          </section>

          {/* ══ 4. Punch card tiers ══ */}
          <section
            className="bg-white rounded-2xl p-4 border border-slate-100 shadow-sm"
            data-testid="activity-punch-section"
          >
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-bold text-slate-800">Punch Cards</h2>
              <span className="text-xs text-slate-500">{punchCards.length} active</span>
            </div>
            {punchCards.length === 0 ? (
              <div className="text-center py-5 text-xs text-slate-400">
                <Coffee className="w-6 h-6 mx-auto mb-1 text-slate-300" />
                No punch cards yet. Start a loyalty streak with a favorite business.
              </div>
            ) : (
              <div className="space-y-2">
                {punchCards.map((c) => (
                  <PunchCardRow key={c.business_id} card={c} />
                ))}
              </div>
            )}
          </section>

          {/* ══ 5. Points history (last 10) ══ */}
          <section
            className="bg-white rounded-2xl p-4 border border-slate-100 shadow-sm"
            data-testid="activity-points-history-section"
          >
            <h2 className="text-sm font-bold text-slate-800 mb-3">Recent Points</h2>
            {pointsHistory.length > 0 ? (
              <div className="space-y-1.5">
                {pointsHistory.slice(0, 10).map((h, i) => (
                  <div
                    key={h.id || i}
                    className="flex items-center justify-between py-1.5 border-b border-slate-50 last:border-0"
                    data-testid={`activity-points-row-${i}`}
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-medium text-slate-700 truncate">
                        {h.rule_name || h.action || h.event_type || h.rule_id || "Activity"}
                      </p>
                      <p className="text-[10px] text-slate-400">
                        {h.created_at ? new Date(h.created_at).toLocaleDateString() : ""}
                      </p>
                    </div>
                    <span className="text-xs font-bold flex-shrink-0" style={{ color: theme.primary }}>
                      +{h.points_awarded ?? h.points ?? 0}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-slate-400 py-2">No points earned yet. Start exploring.</p>
            )}
          </section>

          {/* ══ 6. Contribution history (last 5) ══ */}
          <section
            className="bg-white rounded-2xl p-4 border border-slate-100 shadow-sm"
            data-testid="activity-contributions-section"
          >
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-bold text-slate-800">Your Contributions</h2>
              <span className="text-xs text-slate-500">{contributions.length}</span>
            </div>
            {contributions.length > 0 ? (
              <div className="space-y-1.5">
                {contributions.slice(0, 5).map((c, i) => (
                  <div
                    key={c.id || i}
                    className="flex items-center gap-2.5 py-2 border-b border-slate-50 last:border-0"
                    data-testid={`activity-contribution-row-${i}`}
                  >
                    <div className="w-7 h-7 rounded-lg bg-indigo-50 flex items-center justify-center flex-shrink-0">
                      <FileText className="w-3.5 h-3.5 text-indigo-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-slate-700 truncate">{c.label}</p>
                      <p className="text-[10px] text-slate-400">
                        {(c.subtitle || "")} · {c.created_at ? new Date(c.created_at).toLocaleDateString() : ""}
                      </p>
                    </div>
                    {c.status && (
                      <span
                        className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full whitespace-nowrap ${
                          c.status === "Verified"
                            ? "bg-emerald-100 text-emerald-700"
                            : c.status === "Declined"
                            ? "bg-rose-100 text-rose-700"
                            : c.status === "Under Review"
                            ? "bg-amber-100 text-amber-700"
                            : "bg-slate-100 text-slate-600"
                        }`}
                      >
                        {c.status}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-slate-400 py-2">
                No contributions yet. Submit an offer or verify a business.
              </p>
            )}
          </section>
        </div>
      )}

      {/* Badge detail modal */}
      {badgeDetail && (
        <div
          className="fixed inset-0 z-[95] flex items-center justify-center p-4"
          onClick={() => setBadgeDetail(null)}
          data-testid="activity-badge-modal"
        >
          <div className="absolute inset-0 bg-black/50" />
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="relative bg-white rounded-3xl p-6 max-w-xs w-full text-center"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setBadgeDetail(null)}
              className="absolute top-3 right-3 w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center"
              data-testid="activity-badge-modal-close"
              aria-label="Close"
            >
              <X className="w-4 h-4 text-slate-500" />
            </button>
            <div className="flex justify-center mb-3">
              <Badge badge={badgeDetail} size="feature" />
            </div>
            <h3 className="text-base font-bold text-slate-800">{badgeDetail.name}</h3>
            <p className="text-xs text-slate-500 mt-1">{badgeDetail.description}</p>
            <div className="mt-3">
              {badgeDetail.earned ? (
                <p className="text-[10px] text-emerald-600 font-semibold">
                  Earned {badgeDetail.earned_at ? new Date(badgeDetail.earned_at).toLocaleDateString() : ""}
                </p>
              ) : (
                <p className="text-[10px] text-slate-400 font-semibold">Locked</p>
              )}
            </div>
          </motion.div>
        </div>
      )}

      <BottomNav />
    </div>
  );
}

const TIER_COLORS = {
  bronze:  { bar: "bg-amber-500",  chip: "bg-amber-100 text-amber-700"  },
  silver:  { bar: "bg-slate-400",  chip: "bg-slate-100 text-slate-700"  },
  gold:    { bar: "bg-yellow-500", chip: "bg-yellow-100 text-yellow-700"},
  platinum:{ bar: "bg-sky-500",    chip: "bg-sky-100 text-sky-700"      },
};

function PunchCardRow({ card }) {
  const colors = TIER_COLORS[card.tier] || TIER_COLORS.bronze;
  return (
    <div
      className="flex items-center gap-3 py-2"
      data-testid={`activity-punch-row-${card.business_id}`}
    >
      <div className="w-9 h-9 rounded-xl bg-slate-100 flex items-center justify-center flex-shrink-0 overflow-hidden">
        {card.logo_url ? (
          <img src={card.logo_url} alt="" className="w-full h-full object-cover" />
        ) : (
          <Award className="w-4 h-4 text-slate-400" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <p className="text-xs font-bold text-slate-800 truncate">{card.business_name || "—"}</p>
          <span
            className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full uppercase ${colors.chip}`}
            data-testid={`activity-punch-tier-${card.business_id}`}
          >
            {card.tier}
          </span>
        </div>
        <div className="mt-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
          <div className={`h-full ${colors.bar} transition-all`} style={{ width: `${card.progress_pct}%` }} />
        </div>
        <p className="text-[10px] text-slate-400 mt-1">
          {card.punch_count} / {card.target} punches
        </p>
      </div>
      <ChevronRight className="w-3.5 h-3.5 text-slate-300 flex-shrink-0" />
    </div>
  );
}
